import { useAppContext } from '@/context';
import { logout, updateUserInStorage } from '@/utils/auth';
import { postJson } from '@/utils/fetchOptions';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth, useAuthToken } from './useAuth';
import { useMutation } from './useMutation';

interface UseTeamSelectProps {
  onResponse?: TypeAny;
  teamId?: string;
}

export function useTeamSelect({ onResponse, teamId }: UseTeamSelectProps) {
  const { setAlert } = useAppContext();
  const { authData, setAuthData } = useAuth();
  const token = useAuthToken();
  const queryClient = useQueryClient();

  const {
    isLoading,
    data = {},
    mutate,
    error
  } = useMutation({
    mutationKey: 'team-select',
    mutationFn: async (selectedTeamId?: string) => {
      const effectiveTeamId = selectedTeamId ?? teamId;
      if (!effectiveTeamId) return {};

      return postJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/team/${effectiveTeamId}/select`,
        body: { team_id: effectiveTeamId },
        token
      }).then((response: Api.User | ResponseObject) => {
        if ((response as ResponseExpiredToken).isExpired) {
          logout();
        }

        if ('code' in response) {
          setAlert(response.code);
          return {};
        }

        // Extract user object from response (Api.User has a 'user' property)
        const userObject = 'user' in response ? response.user : (response as Api.UserObject);

        // Update localStorage (persistence) and React state (immediate UI updates)
        updateUserInStorage(userObject);
        setAuthData({ ...authData, user: userObject });

        // Invalidate relevant queries to trigger refetch
        queryClient.invalidateQueries({ queryKey: ['team-get'] });
        queryClient.invalidateQueries({ queryKey: ['client-campaigns-search'] });
        queryClient.invalidateQueries({ queryKey: ['client-campaign'] });
        queryClient.invalidateQueries({ queryKey: ['client-contenthub-paths-v2'] });
        queryClient.invalidateQueries({ queryKey: ['client-contenthub-search'] });

        return response;
      });
    },
    onSuccess: onResponse,
    onError: onResponse
  });

  return {
    isLoading,
    mutate,
    error,
    data
  };
}
